import { Component, Inject, OnDestroy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from '@angular/material/dialog';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { ConfigService } from '../../services/config.service';
import { RetailItemSale } from '../../../models/retail-item-sale';
import { RetailItemStoreService } from '../retail-item.store.service';
import { takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TaxRateNamePipe } from '../../pipes/tax-rate-name.pipe';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { AddCurrencyPrefixDirective } from '../../directives/add-currency-prefix.directive';
import { MatInput } from '@angular/material/input';
import {
  MatFormField,
  MatLabel,
  MatPrefix
} from '@angular/material/form-field';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-edit-retail-item',
  templateUrl: './edit-retail-item.component.html',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    NgIf,
    MatFormField,
    MatLabel,
    MatInput,
    MatPrefix,
    AddCurrencyPrefixDirective,
    MatSelect,
    NgFor,
    MatOption,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    AsyncPipe,
    TaxRateNamePipe
  ]
})
export class EditRetailItemComponent implements OnDestroy {
  form = new FormGroup({
    freetext_description: new FormControl<string>(null),
    unit_price: new FormControl<string>(null, Validators.required),
    quantity: new FormControl<number>(null, Validators.required),
    tax_rate_type: new FormControl<string>(null, Validators.required),
    serial_number: new FormControl<string>(null)
  });

  availableTaxRates$ = this.configService.taxRates.pipe(
    tap((rates) => console.log({ rates }))
  );
  private ngUnsubscribe = new Subject<void>();
  title = 'Edit Item';
  freeText = false;
  addingFreeTextItem = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RetailItemSale,
    private matDialogRef: MatDialogRef<EditRetailItemComponent>,
    private configService: ConfigService,
    private store: RetailItemStoreService
  ) {
    const {
      unit_price,
      quantity,
      tax_rate_type,
      serial_number,
      line_item_description,
      retail_product_id,
      id
    } = data;
    this.form.patchValue({
      freetext_description: line_item_description,
      unit_price: unit_price ? (unit_price / 100).toFixed(2) : '0',
      quantity,
      tax_rate_type,
      serial_number
    });
    this.freeTextEditingSetup(id, retail_product_id, line_item_description);
  }

  private freeTextEditingSetup(
    existingItemId: number | null,
    retail_product_id: number | null,
    line_item_description: string
  ) {
    if (retail_product_id) {
      this.freeText = false;
      this.title = `Editing ${line_item_description}`;
      this.form.get('freetext_description').disable();
    } else {
      this.freeText = true;
      this.form.get('freetext_description').enable();
      if (!existingItemId) {
        this.addingFreeTextItem = true;
        this.form.patchValue({ unit_price: null });
        this.title = 'Adding Freetext Item';
      } else {
        this.title = 'Editing Freetext Item';
      }
    }
  }

  save() {
    this.configService.taxRates
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((taxRates) => {
        const taxRate =
          taxRates.find(
            (taxRate) => taxRate.type === this.form.value.tax_rate_type
          )?.value ?? 0;
        if (this.addingFreeTextItem) {
          this.store.addItemWithCallback({
            item: {
              ...this.form.value,
              tax_rate: taxRate,
              unit_price: parseFloat(this.form.value.unit_price) * 100
            },
            onSuccess: () => this.matDialogRef.close()
          });
          return;
        }
        this.store.update({
          id: this.data.id,
          update: {
            ...this.form.value,
            unit_price: parseFloat(this.form.value.unit_price),
            tax_rate: taxRate
          },
          onSuccess: () => this.matDialogRef.close()
        });
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
